import { useReservationModalTranslation } from "./reservationModalI18n";
// ReservationGroupCommentsPopup
// Group-level comments with optional per-reservation scoping.
// Opens as a portal overlay. Handles create / edit / delete.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "../api/axiosInstance";
import { getBookerDisplayName } from "./bookerPresentation";

const css = `
  .rgc-overlay {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(2, 4, 10, 0.72);
    backdrop-filter: blur(14px) saturate(130%);
    -webkit-backdrop-filter: blur(14px) saturate(130%);
    z-index: 22000;
  }

  .rgc-shell {
    width: min(95%, 700px);
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.13);
    background: linear-gradient(160deg, rgba(18, 20, 40, 0.98), rgba(10, 12, 28, 0.98));
    box-shadow: 0 32px 80px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08);
    overflow: hidden;
  }

  .rgc-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px 12px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
    flex-shrink: 0;
  }

  .rgc-title {
    font-size: 1rem;
    font-weight: 800;
    color: rgba(230,230,247,0.96);
    letter-spacing: 0.2px;
  }

  .rgc-close {
    background: none;
    border: none;
    color: rgba(230,230,247,0.55);
    font-size: 1.3rem;
    cursor: pointer;
    line-height: 1;
    padding: 2px 6px;
    border-radius: 6px;
    transition: color 120ms;
  }
  .rgc-close:hover { color: rgba(230,230,247,0.9); }

  .rgc-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .rgc-form {
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 14px;
    background: rgba(255,255,255,0.03);
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex-shrink: 0;
  }

  .rgc-formTitle {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.45px;
    color: rgba(48,216,255,0.85);
    font-weight: 700;
    margin-bottom: 2px;
  }

  .rgc-scopeRow {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .rgc-scopeLabel {
    font-size: 0.78rem;
    color: rgba(230,230,247,0.65);
    white-space: nowrap;
  }

  .rgc-select {
    flex: 1;
    min-width: 160px;
    background: rgba(7,7,18,0.75);
    border: 1px solid rgba(255,255,255,0.18);
    border-radius: 9px;
    color: rgba(230,230,247,0.92);
    font-size: 0.82rem;
    padding: 5px 8px;
  }
  .rgc-select:focus {
    outline: none;
    border-color: rgba(48,216,255,0.45);
    box-shadow: 0 0 0 0.16rem rgba(48,216,255,0.18);
  }

  .rgc-textarea {
    width: 100%;
    min-height: 80px;
    resize: vertical;
    background: rgba(7,7,18,0.75);
    border: 1px solid rgba(255,255,255,0.18);
    border-radius: 9px;
    color: rgba(230,230,247,0.92);
    font-size: 0.85rem;
    padding: 8px 10px;
    line-height: 1.5;
    box-sizing: border-box;
  }
  .rgc-textarea:focus {
    outline: none;
    border-color: rgba(48,216,255,0.45);
    box-shadow: 0 0 0 0.16rem rgba(48,216,255,0.18);
  }

  .rgc-formFooter {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
  }

  .rgc-importantToggle {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    font-size: 0.8rem;
    color: rgba(230,230,247,0.7);
    user-select: none;
  }

  .rgc-importantToggle input[type="checkbox"] {
    accent-color: #f59e0b;
    width: 15px;
    height: 15px;
    cursor: pointer;
  }

  .rgc-importantToggle.active {
    color: #f59e0b;
  }

  .rgc-btnRow {
    display: flex;
    gap: 6px;
  }

  .rgc-btn {
    border: none;
    border-radius: 9px;
    font-size: 0.78rem;
    font-weight: 700;
    padding: 6px 14px;
    cursor: pointer;
    transition: filter 120ms, transform 120ms;
    letter-spacing: 0.15px;
  }
  .rgc-btn:hover:not(:disabled) { filter: brightness(1.1); transform: translateY(-1px); }
  .rgc-btn:disabled { opacity: 0.45; cursor: not-allowed; }

  .rgc-btn--primary {
    background: linear-gradient(180deg, rgba(48,216,255,0.28), rgba(33,150,243,0.22));
    border: 1px solid rgba(48,216,255,0.5);
    color: rgba(230,230,247,0.96);
  }
  .rgc-btn--ghost {
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.14);
    color: rgba(230,230,247,0.7);
  }
  .rgc-btn--danger {
    background: rgba(255,80,80,0.12);
    border: 1px solid rgba(255,80,80,0.35);
    color: rgba(255,160,160,0.9);
  }

  .rgc-divider {
    height: 1px;
    background: rgba(255,255,255,0.07);
    margin: 2px 0;
    flex-shrink: 0;
  }

  .rgc-listTitle {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.45px;
    color: rgba(230,230,247,0.4);
    font-weight: 700;
    flex-shrink: 0;
  }

  .rgc-empty {
    font-size: 0.85rem;
    color: rgba(230,230,247,0.45);
    text-align: center;
    padding: 18px 0 8px;
  }

  .rgc-comment {
    border: 1px solid rgba(255,255,255,0.09);
    border-radius: 12px;
    background: rgba(255,255,255,0.025);
    padding: 11px 13px;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .rgc-comment.important {
    border-color: rgba(245,158,11,0.4);
    background: rgba(245,158,11,0.06);
  }

  .rgc-commentHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
  }

  .rgc-commentMeta {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .rgc-commentUser {
    font-size: 0.78rem;
    font-weight: 700;
    color: rgba(48,216,255,0.85);
  }

  .rgc-commentDate {
    font-size: 0.74rem;
    color: rgba(230,230,247,0.42);
  }

  .rgc-commentScope {
    font-size: 0.72rem;
    padding: 1px 7px;
    border-radius: 20px;
    border: 1px solid rgba(168,85,247,0.35);
    color: rgba(168,85,247,0.85);
    background: rgba(168,85,247,0.08);
  }

  .rgc-commentScope.group {
    border-color: rgba(52,211,153,0.35);
    color: rgba(52,211,153,0.85);
    background: rgba(52,211,153,0.07);
  }

  .rgc-flagIcon {
    font-size: 0.85rem;
  }

  .rgc-commentBody {
    font-size: 0.86rem;
    color: rgba(230,230,247,0.85);
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .rgc-commentActions {
    display: flex;
    gap: 5px;
    margin-top: 2px;
  }

  body.rgv-brand-scope--light .rgc-form .rgc-formTitle,
  body.rgv-brand-scope--light .rgc-form .rgc-importantToggle {
    color: #172653 !important;
  }

  body.rgv-brand-scope--light .rgc-form .rgc-importantToggle.active {
    color: #9b3445 !important;
  }

  @media (max-height: 820px) { .rgc-shell { zoom: 0.9; } }
  @media (max-height: 720px) { .rgc-shell { zoom: 0.8; } }
  @media (max-height: 620px) { .rgc-shell { zoom: 0.7; } }
`;

function fmtDT(v, locale) {
  if (!v) return "";
  try {
    return new Date(v).toLocaleString(locale, {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "";
  }
}

const EMPTY_FORM = { body: "", reservation_id: "", is_important: false };

export default function ReservationGroupCommentsPopup({
  isOpen,
  onClose,
  reservationGroupId,
  reservations = [],      // array of reservation objects in the group
  group = null,           // group object, used to read booker name
  initialReservationId,  // pre-select a room on open (null = whole group)
  isDraftGroup = false,
  currentUser,
  onCommentsChange,      // callback(list) so parent keeps a copy of the full array
}) {
  const { t: tr, locale } = useReservationModalTranslation();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const textareaRef = useRef(null);

  // Load comments whenever popup opens
  useEffect(() => {
    if (!isOpen || !reservationGroupId) return;
    let cancelled = false;
    setLoading(true);
    const groupParam = isDraftGroup ? "reservation_group_draft_id" : "reservation_group_id";
    axios
      .get(`/api/reservation-group-comments?${groupParam}=${reservationGroupId}`)
      .then(({ data }) => {
        if (!cancelled) {
          setComments(data);
          onCommentsChange?.(data);
        }
      })
      .catch((err) => console.error("[COMMENTS][LOAD]", err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, reservationGroupId, isDraftGroup]);

  // Reset form + visibility when popup opens/closes
  useEffect(() => {
    if (!isOpen) return;
    setForm({ ...EMPTY_FORM, reservation_id: initialReservationId ?? "" });
    setEditingId(null);
    setShowForm(false);
  }, [isOpen, initialReservationId]);

  const handleSave = async () => {
    if (!form.body.trim()) return;
    setSaving(true);
    try {
      let nextComments;
      const scopedRoomId = form.reservation_id || null;
      const groupPayload = isDraftGroup
        ? { reservation_group_draft_id: reservationGroupId }
        : { reservation_group_id: reservationGroupId };
      const roomPayload = isDraftGroup
        ? { reservation_draft_id: scopedRoomId }
        : { reservation_id: scopedRoomId };

      if (editingId) {
        const { data } = await axios.patch(`/api/reservation-group-comments/${editingId}`, {
          body: form.body,
          is_important: form.is_important,
          ...roomPayload,
        });
        nextComments = comments.map((c) => (c.id === editingId ? data : c));
      } else {
        const { data } = await axios.post("/api/reservation-group-comments", {
          ...groupPayload,
          ...roomPayload,
          body: form.body,
          is_important: form.is_important,
          created_by: currentUser?.name || currentUser?.email || "Unknown",
        });
        nextComments = [data, ...comments];
      }
      setComments(nextComments);
      onCommentsChange?.(nextComments);
      setForm({ ...EMPTY_FORM, reservation_id: initialReservationId ?? "" });
      setEditingId(null);
      setShowForm(false);
    } catch (err) {
      console.error("[COMMENTS][SAVE]", err);
      alert(tr("Error saving comment."));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (comment) => {
    setEditingId(comment.id);
    setForm({
      body: comment.body,
      is_important: comment.is_important,
      reservation_id: (isDraftGroup ? comment.reservation_draft_id : comment.reservation_id) ?? "",
    });
    setShowForm(true);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, reservation_id: initialReservationId ?? "" });
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(tr("Delete this comment?"))) return;
    try {
      await axios.delete(`/api/reservation-group-comments/${id}`);
      const next = comments.filter((c) => c.id !== id);
      setComments(next);
      onCommentsChange?.(next);
      if (editingId === id) handleCancelEdit();
    } catch (err) {
      console.error("[COMMENTS][DELETE]", err);
      alert(tr("Error deleting comment."));
    }
  };

  const scopeLabel = (comment) => {
    const scopedReservationId = isDraftGroup ? comment.reservation_draft_id : comment.reservation_id;
    if (!scopedReservationId) {
      const bookerName = getBookerDisplayName(group);
      return { text: bookerName ? tr("Whole group ({name})", { name: bookerName }) : tr("Whole group"), isGroup: true };
    }
    const res = reservations.find((r) => Number(r.id) === Number(scopedReservationId));
    if (res) {
      const mg = res.main_guest;
      const guestName = mg
        ? `${mg.first_name || ""} ${mg.last_name || ""}`.trim() || null
        : null;
      const label = guestName || res.locator || `#${scopedReservationId}`;
      return { text: label, isGroup: false };
    }
    return { text: `#${scopedReservationId}`, isGroup: false };
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="rgc-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{css}</style>
      <div className="rgc-shell">
        {/* Header */}
        <div className="rgc-header">
          <div className="rgc-title"> {tr("Group Comments")} {comments.length > 0 && (
              <span style={{ marginLeft: 8, fontSize: "0.78rem", fontWeight: 500, color: "rgba(230,230,247,0.45)" }}>
                {comments.length} {tr(comments.length === 1 ? "comment" : "comments")}
              </span>
            )}
          </div>
          <button className="rgc-close" onClick={onClose} title={tr("Close")}>×</button>
        </div>

        <div className="rgc-body">
          {/* New comment button or form */}
          {!showForm ? (
            <button
              className="rgc-btn rgc-btn--primary"
              style={{ alignSelf: "flex-start" }}
              onClick={() => { setShowForm(true); setTimeout(() => textareaRef.current?.focus(), 50); }}
            > {tr("+ New comment")} </button>
          ) : (
            <div className="rgc-form">
              <div className="rgc-formTitle">{editingId ? tr("Edit comment") : tr("New comment")}</div>

              {/* Scope selector */}
              <div className="rgc-scopeRow">
                <span className="rgc-scopeLabel">{tr("Applies to:")}</span>
                <select
                  className="rgc-select"
                  value={form.reservation_id}
                  onChange={(e) => setForm((f) => ({ ...f, reservation_id: e.target.value }))}
                >
                  <option value="">{tr("Whole group")}</option>
                  {reservations.map((r) => {
                    const guestName = r.main_guest
                      ? `${r.main_guest.first_name || ""} ${r.main_guest.last_name || ""}`.trim() || null
                      : null;
                    const locator = r.locator || null;
                    const label = locator && guestName
                      ? `${locator} · ${guestName}`
                      : locator || guestName || `#${r.id}`;
                    return (
                      <option key={r.id} value={r.id}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>

              <textarea
                ref={textareaRef}
                className="rgc-textarea"
                placeholder={tr("Write a comment…")}
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              />

              <div className="rgc-formFooter">
                <label className={`rgc-importantToggle${form.is_important ? " active" : ""}`}>
                  <input
                    type="checkbox"
                    checked={form.is_important}
                    onChange={(e) => setForm((f) => ({ ...f, is_important: e.target.checked }))}
                  /> {tr("🚩 Mark as important")} </label>

                <div className="rgc-btnRow">
                  <button className="rgc-btn rgc-btn--ghost" onClick={handleCancelEdit}> {tr("Cancel")} </button>
                  <button
                    className="rgc-btn rgc-btn--primary"
                    onClick={handleSave}
                    disabled={saving || !form.body.trim()}
                  >
                    {saving ? tr("Saving…") : editingId ? tr("Save changes") : tr("Add comment")}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="rgc-divider" />

          {/* Comment list */}
          {loading ? (
            <div className="rgc-empty">{tr("Loading…")}</div>
          ) : comments.length === 0 ? (
            <div className="rgc-empty">{tr("No comments yet for this group.")}</div>
          ) : (
            <>
              <div className="rgc-listTitle">
                {comments.length} {tr(comments.length === 1 ? "comment" : "comments")}
              </div>
              {comments.map((c) => {
                const scope = scopeLabel(c);
                return (
                  <div key={c.id} className={`rgc-comment${c.is_important ? " important" : ""}`}>
                    <div className="rgc-commentHeader">
                      <div className="rgc-commentMeta">
                        <span className="rgc-commentUser">{c.created_by || tr("Unknown")}</span>
                        <span className="rgc-commentDate">{fmtDT(c.createdAt || c.created_at, locale)}</span>
                        <span className={`rgc-commentScope${scope.isGroup ? " group" : ""}`}>
                          {scope.text}
                        </span>
                        {c.is_important && <span className="rgc-flagIcon">🚩</span>}
                      </div>
                    </div>
                    <div className="rgc-commentBody">{c.body}</div>
                    <div className="rgc-commentActions">
                      <button
                        className="rgc-btn rgc-btn--ghost"
                        style={{ padding: "3px 10px", fontSize: "0.73rem" }}
                        onClick={() => handleEdit(c)}
                      > {tr("Edit")} </button>
                      <button
                        className="rgc-btn rgc-btn--danger"
                        style={{ padding: "3px 10px", fontSize: "0.73rem" }}
                        onClick={() => handleDelete(c.id)}
                      > {tr("Delete")} </button>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
